import { findCountryWithAgencyConfigForUser } from 'features/auth/helpers';
import { getSalesOrderId, patchEnableService, putServiceForm } from '../services';
import { ISaleOrderDetail, IServices, TransferTypeEnum } from '../types';
import { extractErrorMessage } from 'features/quotation/helpers';
import { SelectedServiceRef } from './servicesBulkSelection';

export type NotesPatchMode = 'append' | 'replace';

export interface BulkNotesPatch {
	mode: NotesPatchMode;
	value: string;
}

export interface ServicesBulkEditPatch {
	internalNotes?: BulkNotesPatch;
	voucherNotes?: BulkNotesPatch;
	tariffProviderId?: string;
	enable?: boolean;
}

export interface BulkEditRowResult {
	serviceId: string;
	saleOrderId: string;
	reference: string;
	status: 'success' | 'error';
	error?: string;
}

export interface BulkEditPreviewRow {
	serviceId: string;
	saleOrderId: string;
	reference: string;
	internalNotesBefore: string;
	internalNotesAfter: string;
	voucherNotesBefore: string;
	voucherNotesAfter: string;
	tariffProviderBefore: string;
	tariffProviderAfter: string;
	enableBefore: boolean;
	enableAfter: boolean;
	hydrated: boolean;
	loadError?: string;
}

export interface ServiceLoadError {
	serviceId: string;
	saleOrderId: string;
	message: string;
}

export interface LoadServicesForPreviewResult {
	servicesById: Map<string, IServices>;
	errors: ServiceLoadError[];
}

const applyNotesPatch = (current: string | null | undefined, patch: BulkNotesPatch): string => {
	const base = current ?? '';
	if (patch.mode === 'replace') {
		return patch.value;
	}
	if (!patch.value.trim()) {
		return base;
	}
	if (!base.trim()) {
		return patch.value.trim();
	}
	return `${base}\n${patch.value.trim()}`;
};

const resolveProviderId = (provider?: { id?: string | number } | string | null): string | undefined => {
	if (!provider) return undefined;
	if (typeof provider === 'string') return provider;
	return provider.id !== undefined ? String(provider.id) : undefined;
};

const normalizeTariffProviderForPut = (payload: Record<string, unknown>): string | undefined => {
	const existingId = payload.tariffProviderId;
	if (typeof existingId === 'string' && existingId !== 'n/a') {
		delete payload.tariffProvider;
		return existingId;
	}

	const tariffProviderId = resolveProviderId(payload.tariffProvider as { id?: string | number });
	if (tariffProviderId) {
		payload.tariffProviderId = tariffProviderId;
	} else {
		delete payload.tariffProviderId;
	}

	delete payload.tariffProvider;
	return tariffProviderId;
};

const normalizeServiceProviderForPut = (payload: Record<string, unknown>, tariffProviderId?: string): void => {
	const sameTariffServiceProvider = payload.sameTariffServiceProvider === true;
	const existingId = payload.serviceProviderId;

	if (sameTariffServiceProvider && tariffProviderId) {
		payload.serviceProviderId = tariffProviderId;
		delete payload.serviceProvider;
		return;
	}

	if (typeof existingId === 'string' && existingId !== 'n/a') {
		delete payload.serviceProvider;
		return;
	}

	const serviceProviderId = resolveProviderId(payload.serviceProvider as { id?: string | number });
	if (serviceProviderId) {
		payload.serviceProviderId = serviceProviderId;
	} else {
		delete payload.serviceProviderId;
	}

	delete payload.serviceProvider;
};

const normalizeTransferSegmentsForPut = (payload: Record<string, unknown>): void => {
	const segments = payload.transferSegments;
	if (!Array.isArray(segments)) return;

	payload.transferSegments = segments.map((segment) => {
		if (!segment || typeof segment !== 'object') return segment;
		const normalizedSegment = { ...(segment as Record<string, unknown>) };
		const transferType = normalizedSegment.transferType;
		if (transferType !== TransferTypeEnum.InOut && transferType !== TransferTypeEnum.InOutPrivate) {
			delete normalizedSegment.endDate;
		}
		return normalizedSegment;
	});
};

export const prepareServiceRecordForBulkPut = (payload: Record<string, unknown>, country?: string): void => {
	const tariffProviderId = normalizeTariffProviderForPut(payload);
	normalizeServiceProviderForPut(payload, tariffProviderId);
	normalizeTransferSegmentsForPut(payload);

	['created', 'enable', 'billingStatusDetail', 'providerAutomaticPaymentDateLine', 'issueRequest'].forEach((field) => {
		delete payload[field];
	});

	if (!Array.isArray(payload.providerManualPaymentDateLines)) {
		payload.providerManualPaymentDateLines = [];
	}

	if (payload.totalSaleAmount === null) {
		payload.totalSaleAmount = 0;
	}

	if (country !== 'CO') {
		delete payload.currencyRate;
		delete payload.totalSaleAmountInLocalCurrency;
		delete payload.saleAmountInLocalCurrency;
	}
};

export const applyBulkPatchToService = (service: IServices, patch: ServicesBulkEditPatch): IServices => {
	const next = structuredClone(service) as IServices & Record<string, unknown>;

	if (patch.internalNotes) {
		next.internalNotes = applyNotesPatch(next.internalNotes, patch.internalNotes);
	}

	if (patch.voucherNotes) {
		next.voucherNotes = applyNotesPatch(next.voucherNotes, patch.voucherNotes);
	}

	if (patch.tariffProviderId) {
		next.tariffProviderId = patch.tariffProviderId;
		next.sameTariffServiceProvider = true;
		next.serviceProviderId = patch.tariffProviderId;
	}

	return next;
};

const getProviderLabel = (service: IServices): string => {
	const record = service as IServices & Record<string, unknown>;
	if (typeof record.tariffProviderFantasyName === 'string' && record.tariffProviderFantasyName) {
		return record.tariffProviderFantasyName;
	}
	const tariffProvider = record.tariffProvider as { fantasyName?: string; businessName?: string } | undefined;
	if (tariffProvider?.fantasyName || tariffProvider?.businessName) {
		return tariffProvider.fantasyName || tariffProvider.businessName || '—';
	}
	if (service.tariffProviderId) {
		return String(service.tariffProviderId);
	}
	return '—';
};

export const buildBulkEditPreview = (
	selection: SelectedServiceRef[],
	servicesById: Map<string, IServices>,
	patch: ServicesBulkEditPatch,
	providerLabel?: string,
): BulkEditPreviewRow[] =>
	selection.map((item) => {
		const service = servicesById.get(item.serviceId);
		const hydrated = Boolean(service);
		const notLoadedLabel = '— (no cargado)';
		const beforeInternal = service?.internalNotes ?? '';
		const beforeVoucher = service?.voucherNotes ?? '';
		const beforeProvider = service ? getProviderLabel(service) : item.tariffProviderFantasyName || notLoadedLabel;
		const beforeEnable = service?.enable ?? true;
		const afterService = service ? applyBulkPatchToService(service, patch) : null;

		return {
			serviceId: item.serviceId,
			saleOrderId: item.saleOrderId,
			reference: item.reference || item.serviceId,
			internalNotesBefore: hydrated ? beforeInternal || '—' : notLoadedLabel,
			internalNotesAfter: patch.internalNotes
				? hydrated
					? afterService?.internalNotes ?? '—'
					: notLoadedLabel
				: hydrated
				? beforeInternal || '—'
				: notLoadedLabel,
			voucherNotesBefore: hydrated ? beforeVoucher || '—' : notLoadedLabel,
			voucherNotesAfter: patch.voucherNotes
				? hydrated
					? afterService?.voucherNotes ?? '—'
					: notLoadedLabel
				: hydrated
				? beforeVoucher || '—'
				: notLoadedLabel,
			tariffProviderBefore: beforeProvider,
			tariffProviderAfter: patch.tariffProviderId ? providerLabel ?? patch.tariffProviderId : beforeProvider,
			enableBefore: beforeEnable,
			enableAfter: patch.enable !== undefined ? patch.enable : beforeEnable,
			hydrated,
		};
	});

const saleOrderGroupKey = (item: SelectedServiceRef): string => `${item.agencyId ?? 'default'}:${item.saleOrderId}`;

const groupBySaleOrder = (selection: SelectedServiceRef[]): Map<string, SelectedServiceRef[]> => {
	const map = new Map<string, SelectedServiceRef[]>();
	selection.forEach((item) => {
		const key = saleOrderGroupKey(item);
		const current = map.get(key) ?? [];
		current.push(item);
		map.set(key, current);
	});
	return map;
};

const resolveCountryForItem = (item: SelectedServiceRef, fallbackCountry?: string): string | undefined => {
	if (item.agencyId) {
		return findCountryWithAgencyConfigForUser(item.agencyId)?.id ?? fallbackCountry;
	}
	return fallbackCountry;
};

export const loadServicesForPreview = async (
	selection: SelectedServiceRef[],
): Promise<LoadServicesForPreviewResult> => {
	const servicesById = new Map<string, IServices>();
	const errors: ServiceLoadError[] = [];
	const bySaleOrder = groupBySaleOrder(selection);

	await Promise.all(
		Array.from(bySaleOrder.entries()).map(async ([, items]) => {
			const saleOrderId = items[0]?.saleOrderId ?? '';
			const agencyId = items[0]?.agencyId;
			try {
				const { data } = await getSalesOrderId(saleOrderId, { agencyId });
				items.forEach((item) => {
					const service = data.services?.find((svc) => svc.keyId === item.serviceId);
					if (service) {
						servicesById.set(item.serviceId, service);
					} else {
						errors.push({
							serviceId: item.serviceId,
							saleOrderId,
							message: 'Servicio no encontrado en la orden de venta',
						});
					}
				});
			} catch (error) {
				const message = extractErrorMessage(error, 'No se pudo cargar la orden de venta');
				items.forEach((item) => {
					errors.push({ serviceId: item.serviceId, saleOrderId, message });
				});
			}
		}),
	);

	return { servicesById, errors };
};

export const bulkEditNeedsPut = (patch: ServicesBulkEditPatch): boolean =>
	Boolean(patch.internalNotes?.value.trim() || patch.voucherNotes?.value.trim() || patch.tariffProviderId);

const BULK_EDIT_CONCURRENCY = 4;

const runWithConcurrency = async <T>(items: T[], worker: (item: T) => Promise<void>, limit: number): Promise<void> => {
	if (!items.length) return;
	let index = 0;
	const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
		while (index < items.length) {
			const currentIndex = index;
			index += 1;
			await worker(items[currentIndex]);
		}
	});
	await Promise.all(runners);
};

export const executeServicesBulkEdit = async (
	selection: SelectedServiceRef[],
	patch: ServicesBulkEditPatch,
	country?: string,
	onProgress?: (current: number, total: number) => void,
): Promise<BulkEditRowResult[]> => {
	const results: BulkEditRowResult[] = [];
	const bySaleOrder = groupBySaleOrder(selection);
	const saleOrderCache = new Map<string, ISaleOrderDetail>();
	const saleOrderLoadErrors = new Map<string, string>();
	let processed = 0;
	const total = selection.length;

	const needsPut = bulkEditNeedsPut(patch);
	const needsEnablePatch = patch.enable !== undefined;

	const reportProgress = (): void => {
		processed += 1;
		onProgress?.(processed, total);
	};

	if (needsPut) {
		await Promise.all(
			Array.from(bySaleOrder.entries()).map(async ([groupKey, items]) => {
				const saleOrderId = items[0]?.saleOrderId ?? '';
				const agencyId = items[0]?.agencyId;
				try {
					const { data } = await getSalesOrderId(saleOrderId, { agencyId });
					saleOrderCache.set(groupKey, data);
				} catch (error) {
					saleOrderLoadErrors.set(groupKey, extractErrorMessage(error, 'No se pudo cargar la orden de venta'));
				}
			}),
		);
	}

	type BulkTask = { item: SelectedServiceRef; groupKey: string };
	const tasks: BulkTask[] = [];
	bySaleOrder.forEach((items, groupKey) => {
		items.forEach((item) => tasks.push({ item, groupKey }));
	});

	await runWithConcurrency(
		tasks,
		async ({ item, groupKey }) => {
			const loadError = saleOrderLoadErrors.get(groupKey);
			if (loadError) {
				results.push({
					serviceId: item.serviceId,
					saleOrderId: item.saleOrderId,
					reference: item.reference,
					status: 'error',
					error: loadError,
				});
				reportProgress();
				return;
			}

			try {
				if (needsPut) {
					const order = saleOrderCache.get(groupKey);
					const service = order?.services?.find((svc) => svc.keyId === item.serviceId);
					if (!service) {
						throw new Error('Servicio no encontrado en la orden de venta');
					}

					const payload = applyBulkPatchToService(service, patch) as IServices & Record<string, unknown>;
					prepareServiceRecordForBulkPut(payload, resolveCountryForItem(item, country));
					await putServiceForm({
						saleOrderId: item.saleOrderId,
						serviceId: item.serviceId,
						data: payload,
						agencyId: item.agencyId,
					});
				}

				if (needsEnablePatch) {
					await patchEnableService({
						saleOrderId: item.saleOrderId,
						serviceId: item.serviceId,
						enable: patch.enable as boolean,
						agencyId: item.agencyId,
					});
				}

				results.push({
					serviceId: item.serviceId,
					saleOrderId: item.saleOrderId,
					reference: item.reference,
					status: 'success',
				});
			} catch (error) {
				results.push({
					serviceId: item.serviceId,
					saleOrderId: item.saleOrderId,
					reference: item.reference,
					status: 'error',
					error: extractErrorMessage(error, 'Error al actualizar el servicio'),
				});
			}

			reportProgress();
		},
		BULK_EDIT_CONCURRENCY,
	);

	return results;
};

export const hasBulkPatchValues = (patch: ServicesBulkEditPatch): boolean => {
	if (patch.enable !== undefined) {
		return true;
	}
	return Boolean(patch.internalNotes?.value.trim() || patch.voucherNotes?.value.trim() || patch.tariffProviderId);
};
